import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font
} from '@react-pdf/renderer';
import bidiFactory from 'bidi-js';
import reshaper from 'arabic-persian-reshaper';
// fonts
import NotoSansRegular from 'src/assets/fonts/NotoSans-Regular.ttf';
import NotoSansArabic from 'src/assets/fonts/NotoSansArabic-Regular.ttf';
import NotoSansArabicPDF from 'src/assets/fonts/NotoSansArabic-PDF.ttf';
import NotoSansArabicVariable from 'src/assets/fonts/NotoSansArabic-VariableFont_wdth,wght.ttf';
import MalgunGothic from 'src/assets/fonts/malgun-gothic.ttf';
import NotoSansSymbols2 from 'src/assets/fonts/NotoSansSymbols2-Regular.ttf';

// Register fonts with unique family names to avoid conflicts
Font.register({
  family: 'NotoSansLatin',
  src: NotoSansRegular
});

Font.register({
  family: 'NotoSansSymbols',
  src: NotoSansSymbols2
});

// PDF version looks bad even without knowing Arabic.
// Variable version seems to just ditch all numbers and symbols, not sure if the arabic characters are any better than the regular version
Font.register({
  family: 'NotoSansArabic',
  src: NotoSansArabic
});

Font.register({
  family: 'MalgunGothic',
  src: MalgunGothic
});

// Use the same font for Russian as Latin since Cyrillic is supported
Font.register({
  family: 'NotoSansRussian',
  src: NotoSansRegular
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 50,
    paddingRight: 60,
    paddingBottom: 50,
    paddingLeft: 60,
    fontSize: 12,
    color: '#111827',
    fontFamily: 'NotoSansLatin' // Default font
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 40
  },
  headerLeft: {
    width: '75%'
  },
  headerRight: {
    width: '25%',
    textAlign: 'right'
  },
  sectionLabel: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 4
  },
  questionTitle: {
    fontSize: 16,
    fontWeight: 700
  },
  pageIdLabel: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 4
  },
  pageIdValue: {
    fontSize: 16
  },
  questionNumber: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 14
  },
  prompt: {
    fontSize: 16,
    lineHeight: 1.4,
    marginBottom: 28
  },
  answerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 18
  },
  answerMark: {
    width: 28,
    fontSize: 18,
    fontFamily: 'NotoSansSymbols'
  },
  answerText: {
    flex: 1,
    fontSize: 16,
    lineHeight: 1.3
  }
});

export default function ExportAnswers({ exportData }) {
  const bidi = bidiFactory();
  const template_labels = [
    'TMP-L-007' // ad 1, kp 1, ru 1
    // 'TMP-L-008', // ad 5, kp 6, ru 4
    // 'TMP-L-009', // ad 6, kp none, ru 4
    // 'TMP-L-010', // ad/kp/ru none
    // 'TMP-L-011', // ad/kp/ru none
    // 'TMP-L-012', // ad/kp/ru none
    // 'TMP-PE-011', // ad 1, kp 1, ru 1
    // 'TMP-PE-012', // ad/kp/ru none
    // 'TMP-PE-013', // ad 1, kp 1, ru 1
    // 'TMP-PE-017', // ad 1, kp 1, ru 1
    // 'TMP-PE-018', // ad 1, kp 1, ru 1
    // 'TMP-PE-019' // ad 1, kp 1, ru 1
  ];

  const lessonTitle = exportData?.title || 'Untitled Lesson';
  const sections = [];

  exportData?.sections.forEach((section) => {
    const pages = section.pages
      .filter((page) => template_labels.includes(page.template.label))
      .map((page) => ({
        content: page.content,
        id: page.id,
        label: page.label,
        template: page.template.label,
        title: page.title,
        screen: page.screen
      }));

    if (pages.length > 0) {
      sections.push({
        title: section.title,
        label: section.label,
        pages
      });
    }
  });

  const detectLanguage = (text = '') => {
    const str = String(text);
    if (
      /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(
        str
      )
    )
      return 'ar';
    if (
      /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F\uA960-\uA97F\uD7B0-\uD7FF]/.test(
        str
      )
    )
      return 'ko';
    if (/[\u0400-\u04FF\u0500-\u052F\u2DE0-\u2DFF\uA640-\uA69F]/.test(str))
      return 'ru';
    return 'en';
  };

  const formatArabicForPdf = (text = '') => {
    try {
      const str = String(text).trim();
      if (!str) return str;

      // Apply Arabic reshaping
      const reshaped = reshaper.ArabicShaper.convertArabic(str);
      return reshaped;

      //   // Apply bidirectional text algorithm
      //   const embeddingLevels = bidi.getEmbeddingLevels(reshaped, 'rtl');
      // //   returning reorder seems to create a 'cannot read properties of undefined'
      //   const reordered = bidi.getReorderedString(reshaped, embeddingLevels);

      //   return reordered;

      //   // Manual bidirectional text handling - reverse Arabic segments only
      //   const arabicPattern =
      //     /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]+/g;

      //   let result = reshaped;
      //   const matches = [...reshaped.matchAll(arabicPattern)];

      //   // Reverse each Arabic segment while preserving non-Arabic text positions
      //   matches.forEach((match) => {
      //     const arabicText = match[0];
      //     const reversedArabic = arabicText.split('').reverse().join('');
      //     result = result.replace(arabicText, reversedArabic);
      //   });

      //   return result;
    } catch (error) {
      console.warn(
        'Arabic text processing failed, using original text:',
        error
      );
      return String(text);
    }
  };

  const getFontFamily = (lang) => {
    switch (lang) {
      case 'ar':
        return 'NotoSansArabic';
      case 'ko':
        return 'MalgunGothic';
      case 'ru':
        return 'NotoSansRussian';
      default:
        return 'NotoSansLatin';
    }
  };

  const SafeText = ({ children, style = {} }) => {
    console.log('here 9');
    try {
      const rawText = String(children ?? '');
      if (!rawText.trim()) return <Text style={style} />;

      const lang = detectLanguage(rawText);
      const isArabic = lang === 'ar';

      // Process Arabic text, leave others as-is
      const displayText = isArabic ? formatArabicForPdf(rawText) : rawText;

      const textStyle = {
        ...style,
        fontFamily: getFontFamily(lang),
        direction: isArabic ? 'rtl' : 'ltr',
        textAlign: isArabic ? 'right' : style.textAlign || 'left',
        lineHeight: isArabic ? 1.8 : style.lineHeight || 1.3,
        fontSize: style.fontSize || (isArabic ? 14 : 12)
      };

      return <Text style={textStyle}>{displayText}</Text>;
    } catch (error) {
      console.warn('SafeText rendering failed:', error);
      return <Text style={style}>{String(children ?? '')}</Text>;
    }
  };

  // Add error boundary for the entire document
  try {
    return (
      <Document>
        {sections.flatMap((section) =>
          section.pages.flatMap((page) =>
            (page.content?.core || []).map((question, questionIndex) => (
              <Page
                key={`${page.title}-${question.id || questionIndex}`}
                size="LETTER"
                style={styles.page}
              >
                <View style={styles.headerRow}>
                  <View style={styles.headerLeft}>
                    <Text
                      style={[
                        styles.sectionLabel,
                        { fontFamily: 'NotoSansLatin' }
                      ]}
                    >
                      {lessonTitle}
                    </Text>
                    <SafeText style={styles.questionTitle}>
                      {page.title} - Question {questionIndex + 1}
                    </SafeText>
                  </View>

                  <View style={styles.headerRight}>
                    <Text
                      style={[
                        styles.pageIdLabel,
                        { fontFamily: 'NotoSansLatin' }
                      ]}
                    >
                      Page ID:
                    </Text>
                    <Text
                      style={[
                        styles.pageIdValue,
                        { fontFamily: 'NotoSansLatin' }
                      ]}
                    >
                      {page.screen}
                    </Text>
                  </View>
                </View>

                <Text
                  style={[
                    styles.questionNumber,
                    { fontFamily: 'NotoSansLatin' }
                  ]}
                >
                  Question {questionIndex + 1}:
                </Text>

                <SafeText style={styles.prompt}>{question.prompt}</SafeText>

                {(question.lures || []).map((lure, lureIndex) => (
                  <View
                    key={`${question.id || questionIndex}-lure-${lureIndex}`}
                    style={styles.answerRow}
                  >
                    <Text style={styles.answerMark}>
                      {lure.is_correct ? '✓' : '✕'}
                    </Text>
                    <SafeText style={styles.answerText}>
                      {lure.lure_text}
                    </SafeText>
                  </View>
                ))}
              </Page>
            ))
          )
        )}
      </Document>
    );
  } catch (error) {
    console.error('Document generation failed:', error);
    return (
      <Document>
        <Page size="LETTER" style={styles.page}>
          <Text
            style={{ fontFamily: 'NotoSansLatin', fontSize: 16, color: 'red' }}
          >
            Error generating PDF: {error.message}
          </Text>
        </Page>
      </Document>
    );
  }
}
